import React, { useState } from "react";
import { Award, Package } from "lucide-react";

import { formatCurrency, formatQtd, porUnidade } from "../lib/formato";

// Ranking do Top 10 por quantidade vendida, lucro ou faturamento POR UNIDADE.
// O seletor saiu da tela na v1.0.1843 (maio/2026) e voltou em 21/09/2026 a
// pedido do dono (achado 191), com o mesmo desenho de antes.
export const METRICAS = [
  { key: "qtd", label: "Qtd Vendida" },
  { key: "lucro", label: "Lucro" },
  { key: "faturamento", label: "Faturamento" },
];

export const getMetrica = (metrica) => (p) => {
  if (metrica === "qtd") return Number(p.quantidade) || 0;
  if (metrica === "lucro") return Number(p.lucro) || 0;
  // Por unidade, para não premiar só o mais vendido: 6 un × R$ 165 ganha de 10 un × R$ 100.
  const qty = Number(p.quantidade) || 0;
  return qty > 0 ? (Number(p.valor_total) || 0) / qty : 0;
};

// Lucro só considera produto com ficha técnica: sem ficha, lucro = faturamento.
const elegivel = (metrica, valorDe) => (p) =>
  metrica === "lucro" ? (Number(p.cmv_unit) || 0) > 0 && valorDe(p) > 0 : valorDe(p) > 0;

export function top10(produtos, metrica) {
  const valorDe = getMetrica(metrica);
  return [...produtos].filter(elegivel(metrica, valorDe)).sort((a, b) => valorDe(b) - valorDe(a)).slice(0, 10);
}

const GRADIENTE = {
  qtd: { 1: "from-yellow-400 to-orange-500", 2: "from-gray-300 to-gray-500", 3: "from-orange-600 to-orange-800", resto: "from-orange-400 to-orange-500" },
  lucro: { 1: "from-emerald-400 to-green-600", 2: "from-emerald-300 to-emerald-500", 3: "from-green-500 to-green-700", resto: "from-emerald-400 to-emerald-500" },
  faturamento: { 1: "from-yellow-400 to-emerald-500", 2: "from-gray-300 to-emerald-400", 3: "from-emerald-500 to-emerald-700", resto: "from-emerald-400 to-emerald-500" },
};
const MOLDURA = { 1: "bg-yellow-500/15 ring-1 ring-yellow-500/40", 2: "bg-gray-400/15 ring-1 ring-gray-400/40", 3: "bg-orange-700/15 ring-1 ring-orange-700/40" };
const MEDALHA = { 1: "🥇", 2: "🥈", 3: "🥉" };
const AVISO = {
  lucro: ["bg-amber-500/5 border-amber-500/20 text-amber-700 dark:text-amber-400", "ℹ️ Só entram produtos com ficha técnica cadastrada (CMV calculado)."],
  faturamento: ["bg-blue-500/5 border-blue-500/20 text-blue-700 dark:text-blue-400", "ℹ️ Ranking por faturamento POR UNIDADE vendida — ou por quilo/litro, no produto vendido a peso (proporcional, não absoluto)."],
};
const corMargem = (m) => (m >= 50 ? "text-emerald-600 dark:text-emerald-400" : m >= 30 ? "text-green-600 dark:text-green-400"
  : m >= 15 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400");
const rotulo = "text-xs text-muted-foreground uppercase tracking-wider font-semibold";

function Foto({ p }) {
  return (
    <div className="w-11 h-11 rounded-lg bg-muted/60 overflow-hidden flex-shrink-0 border border-border/50">
      {p.photo_url ? (
        <img src={p.photo_url.startsWith("http") ? p.photo_url : `/api${p.photo_url}`} alt={p.nome} className="w-full h-full object-cover" loading="lazy"
          onError={(e) => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} />
      ) : null}
      <div className={`${p.photo_url ? "hidden" : "flex"} w-full h-full items-center justify-center`}>
        <Package className="w-4 h-4 text-muted-foreground/40" />
      </div>
    </div>
  );
}

// Número em destaque (a métrica escolhida) e o de contexto ao lado.
function Metricas({ p, metrica, valor, top3 }) {
  const tam = top3 ? "text-base" : "text-sm";
  if (metrica === "qtd") {
    return (<>
      <div className="text-right sm:min-w-[120px]"><p className={rotulo}>Vendidos</p>
        <p className={`font-mono font-black tabular-nums leading-none ${top3 ? "text-lg" : "text-base"}`}>{formatQtd(p.quantidade, p.unidade)}</p></div>
      <div className="text-right min-w-[110px] hidden sm:block"><p className={rotulo}>Faturamento</p>
        <p className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm tabular-nums leading-none">{formatCurrency(p.valor_total)}</p></div>
    </>);
  }
  if (metrica === "lucro") {
    return (<>
      <div className="text-right sm:min-w-[120px]"><p className={rotulo}>Lucro</p>
        <p className={`font-mono font-black tabular-nums leading-none ${(p.lucro || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"} ${tam}`}>{formatCurrency(valor)}</p></div>
      <div className="text-right min-w-[110px] hidden sm:block"><p className={rotulo}>Margem</p>
        <p className={`font-mono font-bold text-sm tabular-nums leading-none ${corMargem(p.margem_pct || 0)}`}>{(p.margem_pct || 0).toFixed(1).replace(".", ",")}%</p></div>
    </>);
  }
  return (<>
    <div className="text-right sm:min-w-[120px]"><p className={rotulo}>{p.unidade ? `Por ${p.unidade}` : "Por unidade"}</p>
      <p className={`font-mono font-black tabular-nums leading-none text-emerald-600 dark:text-emerald-400 ${tam}`}>{formatCurrency(valor)}<span className="text-[10px] font-normal text-muted-foreground ml-1">{porUnidade(p.unidade)}</span></p></div>
    <div className="text-right min-w-[110px] hidden sm:block"><p className={rotulo}>Total</p>
      <p className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm tabular-nums leading-none">{formatCurrency(p.valor_total)}</p>
      <p className="text-[10px] text-muted-foreground mt-0.5">{formatQtd(p.quantidade, p.unidade)}</p></div>
  </>);
}

function Linha({ p, rank, max, metrica }) {
  const valor = getMetrica(metrica)(p);
  const top3 = rank <= 3;
  const grad = GRADIENTE[metrica][rank] || GRADIENTE[metrica].resto;
  return (
    <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 hover:bg-muted/30 transition-colors">
      <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${MOLDURA[rank] || "bg-muted/40"}`}>
        {MEDALHA[rank] ? <span className="text-xl leading-none">{MEDALHA[rank]}</span> : <span className="text-sm font-black text-muted-foreground tabular-nums">{rank}</span>}
      </div>
      <Foto p={p} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 min-w-0">
          <p className={`text-sm truncate ${top3 ? "font-bold" : "font-medium"}`}>{p.nome}</p>
          {p.categoria && (
            <span className="hidden sm:inline text-[9px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 font-semibold uppercase tracking-wide flex-shrink-0 border border-orange-500/30">{p.categoria}</span>
          )}
        </div>
        <div className="h-2 bg-muted/40 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-300 bg-gradient-to-r ${grad}`} style={{ width: `${Math.max(2, (valor / max) * 100)}%` }} />
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0"><Metricas p={p} metrica={metrica} valor={valor} top3={top3} /></div>
    </div>
  );
}

export default function Top10Produtos({ produtos }) {
  const [metrica, setMetrica] = useState("qtd");
  const lista = top10(produtos, metrica);
  const max = Math.max(1, ...lista.map(getMetrica(metrica)));
  return (
    <div className="bg-card rounded-xl border overflow-hidden" data-e2e="top10-produtos">
      <div className="px-6 py-4 border-b bg-gradient-to-r from-orange-500/10 to-transparent">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2"><Award className="w-5 h-5 text-orange-500" /><h2 className="text-xl font-bold">Top 10 Produtos</h2></div>
          <div className="inline-flex rounded-lg border bg-muted/40 p-0.5 gap-0.5" data-e2e="top10-metricas">
            {METRICAS.map((opt) => (
              <button key={opt.key} type="button" onClick={() => setMetrica(opt.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${metrica === opt.key ? "bg-orange-500 text-white shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-muted/70"}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {AVISO[metrica] && <div className={`px-5 py-2 border-b text-[11px] ${AVISO[metrica][0]}`}>{AVISO[metrica][1]}</div>}
      <div className="divide-y">
        {lista.length === 0
          ? <div className="py-10 text-center text-muted-foreground text-xs">{metrica === "lucro" ? "Nenhum produto com ficha técnica gerou lucro no período." : "Sem dados pra esse ranking no período."}</div>
          : lista.map((p, i) => <Linha key={`${p.id || ""}|${p.nome || i}`} p={p} rank={i + 1} max={max} metrica={metrica} />)}
      </div>
    </div>
  );
}
